import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Target, Trash, Plus, Wand2, Save } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";
import { api, fmtINR } from "../../lib/kostra";

const RESPONSES = [
  { key: "taste", label: "Taste", default: "max" },
  { key: "texture", label: "Texture", default: "max" },
  { key: "appearance", label: "Appearance", default: "max" },
  { key: "yield_pct", label: "Yield %", default: "max" },
  { key: "shelf_life_days", label: "Shelf life", default: "max" },
  { key: "overall", label: "Overall score", default: "max" },
  { key: "cost", label: "Cost / portion", default: "min" },
];

export default function OptimizePanel({ project }) {
  const [objectives, setObjectives] = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // Apply-optimum dialog state
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyMode, setApplyMode] = useState("new"); // 'update' | 'new'
  const [newName, setNewName] = useState("");
  const [applying, setApplying] = useState(false);

  const baseDishName = project?.base_recipe_snapshot?.dish_name || "Recipe";
  const isChefBase = String(project?.base_recipe_id || "").startsWith("chef:");
  const hasBase = !!project?.base_recipe_snapshot;

  useEffect(() => {
    // seed objectives from response keys present in trials
    const keys = new Set();
    (project.trials || []).forEach((t) => {
      Object.entries(t.results || {}).forEach(([k, v]) => {
        if (k.startsWith("_")) return;
        if (typeof v === "number" && !isNaN(v)) keys.add(k);
      });
    });
    if (objectives.length === 0 && keys.size > 0) {
      const seeded = [...keys].slice(0, 3).map((k) => {
        const r = RESPONSES.find((x) => x.key === k);
        return { response_key: k, goal: r?.default || "max", weight: 1.0, exponent: 1.0 };
      });
      setObjectives(seeded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const update = (i, patch) => {
    const next = [...objectives];
    next[i] = { ...next[i], ...patch };
    setObjectives(next);
  };
  const remove = (i) => setObjectives(objectives.filter((_, idx) => idx !== i));
  const add = () => {
    const used = new Set(objectives.map((o) => o.response_key));
    const next = RESPONSES.find((r) => !used.has(r.key));
    if (!next) return;
    setObjectives([...objectives, { response_key: next.key, goal: next.default, weight: 1, exponent: 1 }]);
  };

  const runOptimize = async () => {
    if (objectives.length === 0) { toast.error("Add at least one objective"); return; }
    setRunning(true);
    try {
      const { data } = await api.post(`/rnd/projects/${project.id}/optimize`, { objectives });
      if (!data.ok) {
        toast.error(data.reason || "Optimization failed");
        setResult(null);
      } else {
        setResult(data);
        toast.success("Optimum found");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Optimization failed");
    } finally {
      setRunning(false);
    }
  };

  const factorUnit = (name) => (project.factors || []).find((f) => f.name === name)?.unit || "";

  const openApply = () => {
    setApplyMode(isChefBase ? "new" : "update");
    setNewName(`${baseDishName} — Optimized`);
    setApplyOpen(true);
  };

  const runApply = async () => {
    if (!project.id) { toast.error("Save the project first"); return; }
    if (objectives.length === 0) { toast.error("Define at least one objective"); return; }
    setApplying(true);
    try {
      const payload = {
        objectives,
        save_as: applyMode,
        new_dish_name: applyMode === "new" ? newName.trim() : undefined,
      };
      const { data } = await api.post(`/rnd/projects/${project.id}/apply-optimum`, payload);
      if (!data.ok) { toast.error("Apply failed"); return; }
      if (data.saved_recipe?.id) {
        toast.success(
          applyMode === "update"
            ? `Updated "${data.saved_recipe.dish_name}" with optimum levels`
            : `Saved "${data.saved_recipe.dish_name}" as a new recipe`
        );
      } else {
        toast.success("Optimum computed (preview only)");
      }
      setApplyOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-5" data-testid="rnd-optimize-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="kostra-section-title text-lg">Multi-Objective Optimizer</h3>
          <p className="text-xs text-[#4B5563] mt-1">Weighted desirability (Derringer-Suich). Higher weight = stronger pull on the optimum.</p>
        </div>
        <Button onClick={runOptimize} disabled={running || !project.id || objectives.length === 0}
                className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                data-testid="rnd-optimize-btn">
          <Sparkles size={16} className="mr-1.5" /> {running ? "Optimizing..." : "Find Optimum"}
        </Button>
      </div>

      <div className="kostra-card p-4 space-y-3">
        {objectives.length === 0 && (
          <div className="text-xs text-[#4B5563]">Add an objective to start.</div>
        )}
        {objectives.map((o, i) => {
          const r = RESPONSES.find((x) => x.key === o.response_key);
          return (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`rnd-objective-${i}`}>
              <div className="col-span-3">
                <Label className="kostra-data-label mb-1 block">Response</Label>
                <Select value={o.response_key} onValueChange={(v) => update(i, { response_key: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RESPONSES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="kostra-data-label mb-1 block">Goal</Label>
                <Select value={o.goal} onValueChange={(v) => update(i, { goal: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="max">Maximize</SelectItem>
                    <SelectItem value="min">Minimize</SelectItem>
                    <SelectItem value="target">Target value</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {o.goal === "target" && (
                <div className="col-span-2">
                  <Label className="kostra-data-label mb-1 block">Target</Label>
                  <Input type="number" value={o.target ?? ""} onChange={(e) => update(i, { target: parseFloat(e.target.value || 0) })}
                         className="h-9 font-mono-num" />
                </div>
              )}
              <div className="col-span-4">
                <Label className="kostra-data-label mb-1 block">Weight ({(o.weight || 1).toFixed(1)})</Label>
                <Slider min={0.5} max={5} step={0.5} value={[o.weight || 1]}
                        onValueChange={(v) => update(i, { weight: v[0] })} />
              </div>
              <div className="col-span-1 flex justify-end">
                <button type="button" onClick={() => remove(i)} className="h-9 w-9 rounded-md text-[#4B5563] hover:bg-[#FBE8E8] hover:text-[#DC2626] inline-flex items-center justify-center">
                  <Trash size={14} />
                </button>
              </div>
            </div>
          );
        })}
        <Button type="button" variant="outline" onClick={add} className="border-[#0A3B24]/30 text-[#0A3B24]">
          <Plus size={14} className="mr-1.5" /> Add Objective
        </Button>
      </div>

      {result?.ok && (
        <div className="space-y-4" data-testid="rnd-optimization-result">
          <div className="kostra-card p-5 bg-gradient-to-br from-[#FDF9EE] to-white border border-[#D4AF37]/40">
            <div className="flex items-center gap-2 text-[#D4AF37] uppercase text-xs tracking-[0.2em] font-semibold">
              <Target size={14} /> Optimum found
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <span className="kostra-numeric-xl text-4xl text-[#0A3B24]">{(result.overall_desirability * 100).toFixed(1)}%</span>
              <span className="text-xs text-[#4B5563]">overall desirability</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="kostra-card p-4">
              <h4 className="kostra-section-title text-sm">Optimum Factor Levels</h4>
              <table className="w-full text-sm mt-2">
                <tbody>
                  {Object.entries(result.factor_levels || {}).map(([n, v]) => (
                    <tr key={n} className="border-b">
                      <td className="py-1.5 font-medium">{n}</td>
                      <td className="py-1.5 text-right font-mono-num text-[#0A3B24] font-semibold">{(+v).toFixed(2)} {factorUnit(n)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="kostra-card p-4">
              <h4 className="kostra-section-title text-sm">Predicted Responses</h4>
              <table className="w-full text-sm mt-2">
                <tbody>
                  {Object.entries(result.predicted_responses || {}).map(([k, v]) => (
                    <tr key={k} className="border-b">
                      <td className="py-1.5 font-medium">{k}</td>
                      <td className="py-1.5 text-right font-mono-num text-[#0A3B24] font-semibold">
                        {k === "cost" ? fmtINR(v) : (+v).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {result.optimized_summary && (
            <div className="kostra-card p-4">
              <h4 className="kostra-section-title text-sm">Optimized Recipe Costing</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <Stat label="Total recipe cost" value={fmtINR(result.optimized_summary.total_recipe_cost)} />
                <Stat label="Cost / portion" value={fmtINR(result.optimized_summary.cost_per_portion)} />
                <Stat label="Suggested price" value={fmtINR(result.optimized_summary.suggested_price)} />
                <Stat label="Margin" value={`${result.optimized_summary.gross_margin_pct.toFixed(1)}%`} />
              </div>
            </div>
          )}

          {/* Apply optimum CTA */}
          {hasBase && (
            <div className="kostra-card p-5 bg-gradient-to-br from-[#FDF9EE] to-white border border-[#D4AF37]/30 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h4 className="kostra-section-title text-sm flex items-center gap-2">
                  <Wand2 size={14} className="text-[#D4AF37]" /> Apply optimum to base recipe
                </h4>
                <p className="text-xs text-[#4B5563] mt-1 max-w-xl">
                  Push these factor levels back into <strong>{baseDishName}</strong> and either
                  update the saved recipe or save the optimized version as a new recipe.
                </p>
              </div>
              <Button onClick={openApply}
                      className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold whitespace-nowrap"
                      data-testid="rnd-apply-optimum-btn">
                <Save size={14} className="mr-1.5" /> Apply to Recipe
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Apply-optimum dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-md" data-testid="rnd-apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#0A3B24]">Apply Optimum to Recipe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="kostra-data-label mb-1.5 block">Save as</Label>
              <Select value={applyMode} onValueChange={setApplyMode}>
                <SelectTrigger className="h-10" data-testid="rnd-apply-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!isChefBase && (
                    <SelectItem value="update">Update the existing saved recipe</SelectItem>
                  )}
                  <SelectItem value="new">Save as a new recipe</SelectItem>
                </SelectContent>
              </Select>
              {isChefBase && (
                <p className="text-[11px] text-[#4B5563] mt-1.5">
                  Base is a chef recipe (read-only). The optimum will be saved as a new recipe in your library.
                </p>
              )}
            </div>
            {applyMode === "new" && (
              <div>
                <Label className="kostra-data-label mb-1.5 block">New Recipe Name</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="h-10"
                       data-testid="rnd-apply-new-name" />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setApplyOpen(false)}
                      className="border-[#E5E7EB] text-[#4B5563]">Cancel</Button>
              <Button onClick={runApply} disabled={applying}
                      className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                      data-testid="rnd-apply-confirm">
                <Save size={14} className="mr-1.5" />
                {applying ? "Applying..." : (applyMode === "update" ? "Update Recipe" : "Save New Recipe")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-3">
      <div className="kostra-data-label">{label}</div>
      <div className="kostra-numeric-xl text-xl text-[#0A3B24] mt-1">{value}</div>
    </div>
  );
}
